import {assert} from 'Asserts';
import {buildAdjacencyMap} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/TraverseFlowchart';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';
import runModal from 'ui-library/runModal';

import {getFieldInfoFromDataFlowEdgeHandle} from '../../flowchartIdentifierUtils';
import {
  CustomMappingData,
  FlowchartStaticEdgeData,
  ProcessFlowchartEdgeType,
  StaticTemplateFlowchart,
} from '../../processFlowchartConfigEditorConstants';
import {
  isStaticRouterNodeData,
  isStaticTaskSchemaNodeData,
  StaticFlowchartNodeData,
  TaskSchemaDomainData,
} from '../../processFlowchartConfigEditorNodeTypes';
import {DeletedNodeContext} from '../../processFlowchartConfigEditorUtils';
import PromoteTransitionModal from '../../PromoteTransitionModal';
import {
  buildTransitionIndexByPTDVPair,
  createEmptyTransition,
  getFieldPairKey,
  getOrCreateTransition,
  getPTDVPairKey,
  getTransitionId,
} from './transitionKeyUtils';
import {Transition} from './TransitionTypes';

export interface CreateTransitionResult {
  transition: Transition;
  /** Whether a new transition was created */
  isNew: boolean;
}
/**
 * Resolves the source and target PTDV IDs for an edge.
 * When a node has been deleted, its PTDV ID can no longer be found in nodesStaticData.
 * In that case, deletedNodeContext provides the PTDV IDs captured before removal.
 */
export function getPTDVIdsFromEdge(
  edge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>,
  deletedNodeContext?: DeletedNodeContext
): {sourcePTDVId: string; targetPTDVId: string} | null {
  const sourceNode = nodes.find((n) => n.id === edge.source);
  const targetNode = nodes.find((n) => n.id === edge.target);

  const sourcePTDVId =
    sourceNode && isStaticTaskSchemaNodeData(sourceNode)
      ? sourceNode.taskDefinitionVersionSpec.clientId
      : deletedNodeContext?.deletedSourceNodePTDVId;

  const targetPTDVId =
    targetNode && isStaticTaskSchemaNodeData(targetNode)
      ? targetNode.taskDefinitionVersionSpec.clientId
      : deletedNodeContext?.deletedTargetNodePTDVId;

  if (sourcePTDVId == null || targetPTDVId == null) {
    return null;
  }

  return {sourcePTDVId, targetPTDVId};
}

export function getFieldPairKeyFromDataFlowEdge(
  edge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>,
  deletedNodeContext?: DeletedNodeContext
): string | null {
  if (edge.type !== ProcessFlowchartEdgeType.DATA_FLOW) {
    return null;
  }

  const ptdvIds = getPTDVIdsFromEdge(edge, nodes, deletedNodeContext);
  if (!ptdvIds) {
    return null;
  }

  const sourceHandle = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle);
  const targetHandle = getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle);

  return getFieldPairKey({
    sourcePTDVId: ptdvIds.sourcePTDVId,
    sourceSectionType: sourceHandle.sectionType,
    sourceFieldClientId: sourceHandle.fieldClientId,
    targetPTDVId: ptdvIds.targetPTDVId,
    targetSectionType: targetHandle.sectionType,
    targetFieldClientId: targetHandle.fieldClientId,
  });
}

export function getOrCreateTransitionForEdge(
  edge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>,
  transitions: Record<string, Transition>
): CreateTransitionResult | null {
  if (edge.type !== ProcessFlowchartEdgeType.CONTROL_FLOW) {
    return null;
  }

  const ptdvIds = getPTDVIdsFromEdge(edge, nodes);
  if (!ptdvIds) {
    return null;
  }

  const {sourcePTDVId, targetPTDVId} = ptdvIds;
  const index = buildTransitionIndexByPTDVPair(transitions);
  const pairKey = getPTDVPairKey(sourcePTDVId, targetPTDVId);
  const existingTransitionId = index[pairKey]?.[0];

  if (existingTransitionId != null && transitions[existingTransitionId]) {
    return {
      transition: transitions[existingTransitionId],
      isNew: false,
    };
  }

  const newTransition = createEmptyTransition({
    sourcePTDVIds: [sourcePTDVId],
    targetPTDVId,
  });
  return {
    transition: newTransition,
    isNew: true,
  };
}

export interface OrphanedSourcePTDV {
  transitionId: string;
  sourcePTDVId: string;
}

export interface FindOrphanedTransitionIdsAndNodePairsResult {
  orphanedTransitionIds: Array<string>;
  orphanedSourcePTDV: Array<OrphanedSourcePTDV>;
}

function buildNodeToPTDVIdMap(
  nodes: Array<StaticFlowchartNodeData>,
  removedEdge: FlowchartStaticEdgeData,
  deletedNodeContext?: DeletedNodeContext
): Map<string, string> {
  const nodeToPTDVId = new Map<string, string>();
  for (const node of nodes) {
    if (isStaticTaskSchemaNodeData(node)) {
      nodeToPTDVId.set(node.id, node.taskDefinitionVersionSpec.clientId);
    }
  }
  // When a router is deleted, the context carries the "effective" PTDV that flows
  // through the router (for pair resolution in getPTDVIdsFromEdge). But the router
  // node itself must not be mapped — it has no PTDV of its own, and the BFS in
  // hasRemainingPathBetweenPTDVs needs to traverse through unmapped nodes.
  if (!deletedNodeContext?.deletedNodeWasRouter) {
    if (deletedNodeContext?.deletedSourceNodePTDVId != null) {
      nodeToPTDVId.set(removedEdge.source, deletedNodeContext.deletedSourceNodePTDVId);
    }
    if (deletedNodeContext?.deletedTargetNodePTDVId != null) {
      nodeToPTDVId.set(removedEdge.target, deletedNodeContext.deletedTargetNodePTDVId);
    }
  }
  return nodeToPTDVId;
}

/**
 * Checks whether any path exists from a node mapped to sourcePTDVId to a node
 * mapped to targetPTDVId using the pre-built adjacency map. Intermediate nodes
 * without PTDV mappings (e.g. router nodes) are traversed through.
 */
function hasRemainingPathBetweenPTDVs(
  adjacencyMap: Record<string, Array<string>>,
  nodeToPTDVId: Map<string, string>,
  sourcePTDVId: string,
  targetPTDVId: string
): boolean {
  const startNodeIds: Array<string> = [];
  for (const [nodeId, ptdvId] of nodeToPTDVId.entries()) {
    if (ptdvId === sourcePTDVId) {
      startNodeIds.push(nodeId);
    }
  }

  if (startNodeIds.length === 0) {
    return false;
  }

  const visited = new Set<string>(startNodeIds);
  const queue = [...startNodeIds];
  let i = 0;

  while (i < queue.length) {
    const currentNodeId = queue[i++];

    for (const nextNodeId of adjacencyMap[currentNodeId] ?? []) {
      if (visited.has(nextNodeId)) {
        continue;
      }
      visited.add(nextNodeId);

      if (nodeToPTDVId.get(nextNodeId) === targetPTDVId) {
        return true;
      }

      if (!nodeToPTDVId.has(nextNodeId)) {
        queue.push(nextNodeId);
      }
    }
  }

  return false;
}

/**
 * Returns transitions to delete and sources to remove after an edge was removed.
 *
 * A transition is orphaned when no control flow or data flow edges remain between
 * the same PTDV pair. This handles both control flow and data flow edge removals:
 * - Removing a control flow edge keeps the transition if data flow edges remain
 *   between the removed edge's PTDV pair (preserves custom mapping data during
 *   restructuring, e.g., inserting a router).
 * - Removing a data flow edge uses per-source orphan logic: for each source in the
 *   transition, if no edges of any type remain to the target, that source is orphaned.
 *   If all sources are orphaned, the entire transition is deleted.
 *
 * For merging transitions (transitions with multiple source PTDVs), if all control
 * flow edges for a specific source PTDV are removed but other sources still have
 * edges, that source is returned as an orphaned source to be removed from the
 * transition. If ALL sources lose their edges, the entire transition is orphaned.
 * Data flow edges between the removed edge's PTDV pair protect the entire transition
 * from any orphaning (both full-transition and per-source).
 *
 * e.g. if we have a transition {A, B} -> C and remove the control flow edge from
 * A to C (but B -> C remains), the source A is returned as orphaned. However, if
 * data flow edges from A -> C still exist, the transition is kept intact.
 *
 * When a node has been deleted, its PTDV ID can no longer be resolved from nodesStaticData.
 * In that case, the caller passes deletedNodeContext with the PTDV IDs captured before the
 * node was removed, so we can still identify the affected transition.
 */
export function findOrphanedTransitionAndSourcePTDVIds(
  removedEdge: FlowchartStaticEdgeData,
  remainingEdges: Array<FlowchartStaticEdgeData>,
  nodes: Array<StaticFlowchartNodeData>,
  ptdvPairToTransitionIdIndex: Record<string, Array<string>>,
  transitions: Record<string, Transition>,
  deletedNodeContext?: DeletedNodeContext
): FindOrphanedTransitionIdsAndNodePairsResult {
  const EMPTY_RESULT: FindOrphanedTransitionIdsAndNodePairsResult = {
    orphanedTransitionIds: [],
    orphanedSourcePTDV: [],
  };

  const ptdvIds = getPTDVIdsFromEdge(removedEdge, nodes, deletedNodeContext);
  if (!ptdvIds) {
    return EMPTY_RESULT;
  }

  const {sourcePTDVId, targetPTDVId} = ptdvIds;
  const pairKey = getPTDVPairKey(sourcePTDVId, targetPTDVId);
  const transitionIds = ptdvPairToTransitionIdIndex[pairKey];
  if (transitionIds == null || transitionIds.length === 0) {
    return EMPTY_RESULT;
  }

  const nodeToPTDVId = buildNodeToPTDVIdMap(nodes, removedEdge, deletedNodeContext);

  if (removedEdge.type === ProcessFlowchartEdgeType.CONTROL_FLOW) {
    const hasRemainingDataFlowEdge = remainingEdges.some(
      (edge) =>
        edge.type === ProcessFlowchartEdgeType.DATA_FLOW &&
        nodeToPTDVId.get(edge.source) === sourcePTDVId &&
        nodeToPTDVId.get(edge.target) === targetPTDVId
    );

    if (hasRemainingDataFlowEdge) {
      return EMPTY_RESULT;
    }

    const controlFlowAdjacency = buildAdjacencyMap({
      edges: remainingEdges,
      shouldIncludeEdge: (edge) => edge.type === ProcessFlowchartEdgeType.CONTROL_FLOW,
    });

    const result: FindOrphanedTransitionIdsAndNodePairsResult = {
      orphanedTransitionIds: [],
      orphanedSourcePTDV: [],
    };

    for (const transitionId of transitionIds) {
      const transition = transitions[transitionId];
      if (!transition) {
        continue;
      }

      const orphanedSourceIds = transition.sourcePTDVIds.filter(
        (sourceId) =>
          !hasRemainingPathBetweenPTDVs(controlFlowAdjacency, nodeToPTDVId, sourceId, targetPTDVId)
      );

      if (orphanedSourceIds.length === transition.sourcePTDVIds.length) {
        result.orphanedTransitionIds.push(transitionId);
      } else {
        for (const orphanedSourceId of orphanedSourceIds) {
          result.orphanedSourcePTDV.push({transitionId, sourcePTDVId: orphanedSourceId});
        }
      }
    }

    return result;
  }

  // Data flow edge removal: per-source orphan check using all edge types.
  // For multi-source transitions like {p1, p3} -> p2, only orphan sources that
  // have no remaining edges to the target.
  const allEdgesAdjacency = buildAdjacencyMap({
    edges: remainingEdges,
    shouldIncludeEdge: () => true,
  });

  const dataFlowResult: FindOrphanedTransitionIdsAndNodePairsResult = {
    orphanedTransitionIds: [],
    orphanedSourcePTDV: [],
  };

  for (const transitionId of transitionIds) {
    const transition = transitions[transitionId];
    if (!transition) {
      continue;
    }

    const orphanedSourceIds = transition.sourcePTDVIds.filter(
      (sourceId) => !hasRemainingPathBetweenPTDVs(allEdgesAdjacency, nodeToPTDVId, sourceId, targetPTDVId)
    );

    if (orphanedSourceIds.length === transition.sourcePTDVIds.length) {
      dataFlowResult.orphanedTransitionIds.push(transitionId);
    } else {
      for (const orphanedSourceId of orphanedSourceIds) {
        dataFlowResult.orphanedSourcePTDV.push({transitionId, sourcePTDVId: orphanedSourceId});
      }
    }
  }

  return dataFlowResult;
}

/**
 * Find all transitions affected by changes to nodes or edges.
 * Useful for determining which transitions need updates when the flowchart changes.
 */
export function getAffectedTransitions(
  changedNodeIds: Array<string>,
  nodes: Array<StaticFlowchartNodeData>,
  transitions: Record<string, Transition>
): Array<string> {
  if (changedNodeIds.length === 0) {
    return [];
  }

  const changedPTDVIds = new Set<string>();
  for (const nodeId of changedNodeIds) {
    const node = nodes.find((n) => n.id === nodeId);
    if (node && isStaticTaskSchemaNodeData(node)) {
      changedPTDVIds.add(node.taskDefinitionVersionSpec.clientId);
    }
  }

  if (changedPTDVIds.size === 0) {
    return [];
  }

  const affectedTransitionIds: Array<string> = [];
  for (const transition of Object.values(transitions)) {
    const involvesPTDV =
      transition.sourcePTDVIds.some((id) => changedPTDVIds.has(id)) ||
      changedPTDVIds.has(transition.targetPTDVId);

    if (involvesPTDV) {
      affectedTransitionIds.push(transition.id);
    }
  }

  return affectedTransitionIds;
}

export function addCustomMappingForDataFlowEdge(
  edge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>,
  transition: Transition,
  customMappingData: CustomMappingData | null = null
): Record<string, CustomMappingData | null> | null {
  const fieldPairKey = getFieldPairKeyFromDataFlowEdge(edge, nodes);
  if (!fieldPairKey) {
    return null;
  }

  return {
    ...transition.customMappingDataByFieldPair,
    [fieldPairKey]: customMappingData,
  };
}

/**
 * Clears the custom mapping for a data flow edge
 */
export function removeCustomMappingForDataFlowEdge(
  edge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>,
  transition: Transition
): Record<string, CustomMappingData | null> | null {
  const fieldPairKey = getFieldPairKeyFromDataFlowEdge(edge, nodes);
  if (!fieldPairKey) {
    return null;
  }

  return {
    ...transition.customMappingDataByFieldPair,
    [fieldPairKey]: null,
  };
}

/**
 * Removes a field pair entirely from the transition when a data flow edge is deleted.
 */
export function removeFieldPairForDataFlowEdge(
  edge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>,
  transition: Transition,
  deletedNodeContext?: DeletedNodeContext
): Record<string, CustomMappingData | null> | null {
  const fieldPairKey = getFieldPairKeyFromDataFlowEdge(edge, nodes, deletedNodeContext);
  if (!fieldPairKey) {
    return null;
  }

  const {[fieldPairKey]: _, ...remainingMappings} = transition.customMappingDataByFieldPair;
  return remainingMappings;
}

/**
 * Removes a source PTDV from a transition and re-keys it in the transitions record.
 * Since transition IDs are derived from sourcePTDVIds + targetPTDVId, removing a source
 * requires updating the ID and the record key to keep the transition reachable.
 */
export function removeSourcePTDVFromTransition(
  transitions: Record<string, Transition>,
  transitionId: string,
  sourcePTDVId: string
): void {
  const transition = transitions[transitionId];
  if (!transition) {
    return;
  }

  transition.sourcePTDVIds = transition.sourcePTDVIds.filter((id) => id !== sourcePTDVId);
  delete transition.transitionSourcesBySourcePTDVId[sourcePTDVId];
  const prefix = `${sourcePTDVId}:`;
  for (const key of Object.keys(transition.customMappingDataByFieldPair)) {
    if (key.startsWith(prefix)) {
      delete transition.customMappingDataByFieldPair[key];
    }
  }

  const newId = getTransitionId(transition.sourcePTDVIds, transition.targetPTDVId);
  if (newId !== transitionId) {
    transition.id = newId;
    transitions[newId] = transition;
    delete transitions[transitionId];
  }
}

/**
 * Creates transitions for a newly added control flow edge.
 * Handles three cases:
 * 1. Direct task-to-task edge: Creates a single transition
 * 2. Router-to-task edge: Creates transitions from all upstream task nodes to the target
 * 3. Task-to-router edge: Creates transitions from the source to all downstream task nodes
 *
 * Skips creation when an existing transition already covers the source→target pair
 * (e.g. a merging transition created in the modal that includes the source PTDV).
 *
 * @returns Array of new transitions to be added
 */
export function createTransitionsForNewControlFlowEdge(
  newEdge: FlowchartStaticEdgeData,
  nodes: Array<StaticFlowchartNodeData>,
  allEdges: Array<FlowchartStaticEdgeData>,
  existingTransitions: Record<string, Transition>
): Array<Transition> {
  if (newEdge.type !== ProcessFlowchartEdgeType.CONTROL_FLOW) {
    return [];
  }
  const newTransitions: Array<Transition> = [];
  const workingTransitions = {...existingTransitions};

  for (const {sourceNode, targetNode} of getTaskSchemaNodesForEdge(newEdge, nodes, allEdges)) {
    const sourcePTDVId = sourceNode.taskDefinitionVersionSpec.clientId;
    const targetPTDVId = targetNode.taskDefinitionVersionSpec.clientId;

    const {transition, isNew} = getOrCreateTransition({
      sourcePTDVIds: [sourcePTDVId],
      targetPTDVId,
      transitions: workingTransitions,
      isPlanned: true,
    });
    if (isNew) {
      for (const edge of allEdges) {
        if (edge.type !== ProcessFlowchartEdgeType.DATA_FLOW) {
          continue;
        }
        const ptdvIds = getPTDVIdsFromEdge(edge, nodes);
        if (ptdvIds?.sourcePTDVId === sourcePTDVId && ptdvIds.targetPTDVId === targetPTDVId) {
          const updatedMappings = addCustomMappingForDataFlowEdge(edge, nodes, transition);
          if (updatedMappings) {
            transition.customMappingDataByFieldPair = updatedMappings;
          }
        }
      }
      newTransitions.push(transition);
    }
  }
  return newTransitions;
}

/**
 * Gets the relevant task schema node pairs for a given edge, which can be used
 * to identify affected transitions.  Handles the three cases of task-to-task,
 * router-to-task, and task-to-router
 */
export function getTaskSchemaNodesForEdge(
  edge: Pick<FlowchartStaticEdgeData, 'source' | 'target'>,
  nodes: Array<StaticFlowchartNodeData>,
  allEdges: Array<FlowchartStaticEdgeData>
): Array<{sourceNode: TaskSchemaDomainData; targetNode: TaskSchemaDomainData}> {
  const sourceNode = nodes.find((n) => n.id === edge.source);
  const targetNode = nodes.find((n) => n.id === edge.target);

  if (sourceNode == null || targetNode == null) {
    return [];
  }

  const taskSchemaNodePairs: Array<{
    sourceNode: TaskSchemaDomainData;
    targetNode: TaskSchemaDomainData;
  }> = [];

  // Case 1: Direct task-to-task edge
  if (isStaticTaskSchemaNodeData(sourceNode) && isStaticTaskSchemaNodeData(targetNode)) {
    taskSchemaNodePairs.push({sourceNode, targetNode});
  }

  // Case 2: Router-to-task edge - find all upstream task nodes
  if (isStaticRouterNodeData(sourceNode) && isStaticTaskSchemaNodeData(targetNode)) {
    const incomingEdges = allEdges.filter(
      (e) => e.target === sourceNode.id && e.type === ProcessFlowchartEdgeType.CONTROL_FLOW
    );
    assert(incomingEdges.length <= 1, 'Expected at most one incoming edge');
    const upstreamNode = nodes.find((n) => n.id === incomingEdges[0]?.source);
    if (upstreamNode != null && isStaticTaskSchemaNodeData(upstreamNode)) {
      taskSchemaNodePairs.push({sourceNode: upstreamNode, targetNode});
    }
  }

  // Case 3: Task-to-router edge - find all downstream task nodes
  if (isStaticTaskSchemaNodeData(sourceNode) && isStaticRouterNodeData(targetNode)) {
    const outgoingEdges = allEdges.filter(
      (e) => e.source === targetNode.id && e.type === ProcessFlowchartEdgeType.CONTROL_FLOW
    );
    for (const outgoingEdge of outgoingEdges) {
      const downstreamNode = nodes.find((n) => n.id === outgoingEdge.target);
      if (downstreamNode != null && isStaticTaskSchemaNodeData(downstreamNode)) {
        taskSchemaNodePairs.push({sourceNode, targetNode: downstreamNode});
      }
    }
  }

  return taskSchemaNodePairs;
}

/**
 * Gets optional transitions for a newly added control flow edge
 *
 * @returns Array of optional transitions to be promoted
 */
export function getOptionalTransitionsForNewEdge(
  newEdge: Pick<FlowchartStaticEdgeData, 'source' | 'target'>,
  nodes: Array<StaticFlowchartNodeData>,
  allEdges: Array<FlowchartStaticEdgeData>,
  transitions: Record<string, Transition>
): Array<Transition> {
  const optionalTransitions: Array<Transition> = [];

  for (const {sourceNode, targetNode} of getTaskSchemaNodesForEdge(newEdge, nodes, allEdges)) {
    const sourcePTDVId = sourceNode.taskDefinitionVersionSpec.clientId;
    const targetPTDVId = targetNode.taskDefinitionVersionSpec.clientId;

    const transitionId = getTransitionId([sourcePTDVId], targetPTDVId);
    const existing = transitions[transitionId];

    if (existing && !existing.isPlanned) {
      optionalTransitions.push(existing);
    }
  }
  return optionalTransitions;
}

/**
 * Checks if a new edge should be added.
 *
 * If the edge represents a new transition or an existing planned transition,
 * returns true to indicate the edge should be added.
 *
 * If the edge represents an optional transition, shows a modal to ask the user
 * if they want to add the transition as a planned transition, which would keep
 * the edge, or if they want to cancel adding the edge.
 *
 * @returns A promise that resolves to true if the edge should be added, or false if it should not be added.
 */
export async function shouldAddEdge({
  edge,
  staticFlowchart,
  ptdvPairToTransitionIdIndex,
  transitions,
}: {
  edge: Pick<FlowchartStaticEdgeData, 'source' | 'target' | 'type'>;
  staticFlowchart: StaticTemplateFlowchart;
  ptdvPairToTransitionIdIndex: Record<string, Array<string>>;
  transitions: Record<string, Transition>;
}): Promise<boolean> {
  if (edge.type !== ProcessFlowchartEdgeType.CONTROL_FLOW) {
    return true;
  }
  const optionalTransitions = getOptionalTransitionsForNewEdge(
    edge,
    staticFlowchart.nodesStaticData,
    staticFlowchart.edgesStaticData,
    transitions
  );

  const getNodeName = (ptdvClientId: string): string => {
    const node = staticFlowchart.nodesStaticData.find(
      (n) => isStaticTaskSchemaNodeData(n) && n.taskDefinitionVersionSpec.clientId === ptdvClientId
    );
    assert(
      node != null && isStaticTaskSchemaNodeData(node),
      'Expected to find task schema node for PTDV client ID'
    );
    const adapter = PLUGIN_REGISTRY_INSTANCE.getTaskSchemaNodeAdapter(
      node.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.executionType
    );
    return adapter.getNodeName(node.taskDefinitionVersionSpec);
  };

  const transitionNames: Record<string, {sourceNodeName: string; targetNodeName: string}> = {};
  for (const optionalTransition of optionalTransitions) {
    // TODO(BNCH-147866): Handle merging transitions
    assert(
      optionalTransition.sourcePTDVIds.length === 1,
      'Expected optional transition to have one source PTDV ID'
    );
    const pairKey = getPTDVPairKey(optionalTransition.sourcePTDVIds[0], optionalTransition.targetPTDVId);
    const ids = ptdvPairToTransitionIdIndex[pairKey] ?? [];
    for (const id of ids) {
      // TODO(BNCH-148130): The check for transitions[id] existing is for
      // the single transition modal configuration, which shouldn't open the
      // modal, but might in some cases since the modal will think it's a
      // promotion. This can probably be removed after some single transition
      // modal changes
      if (transitions[id]) {
        transitionNames[pairKey] = {
          sourceNodeName: getNodeName(optionalTransition.sourcePTDVIds[0]),
          targetNodeName: getNodeName(optionalTransition.targetPTDVId),
        };
      }
    }
  }

  if (Object.keys(transitionNames).length > 0) {
    const result = await runModal(PromoteTransitionModal, {
      transitionNames: Object.values(transitionNames),
    });
    return result != null;
  }
  return true;
}
